import React from "react";
import { shortTeamName } from "./ui/MatchRow";
import { getFixturePredictionDeadline } from "../utils/fixtureDeadline";

const RESULT_CLASS = {
  W: "bg-green-600 text-white border-green-600",
  D: "bg-amber-400 text-amber-950 border-amber-400",
  L: "bg-red-600 text-white border-red-600",
};

function canon(insight, name) {
  const raw = String(name || "").trim();
  if (!raw) return "";
  return insight?.names?.[raw.toLowerCase()] || raw;
}

function pairKey(insight, home, away) {
  return [canon(insight, home), canon(insight, away)].sort().join("|");
}

function formatLock(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function FormDots({ name, results }) {
  if (!results?.length) return null;
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${name} form ${results.join("")}`}>
      {results.map((result, index) => (
        <span
          key={`${result}-${index}`}
          className={`flex h-[16px] w-[16px] items-center justify-center rounded-[3px] border text-[10px] font-bold leading-none ${
            RESULT_CLASS[result] || RESULT_CLASS.D
          }`}
        >
          {result}
        </span>
      ))}
    </span>
  );
}

export default function MatchInsight({ match, insight }) {
  const home = match?.home_team;
  const away = match?.away_team;
  const homeCanon = canon(insight, home);
  const awayCanon = canon(insight, away);
  const homePos = insight?.positions?.[homeCanon];
  const awayPos = insight?.positions?.[awayCanon];
  const homeForm = insight?.form?.[homeCanon] || [];
  const awayForm = insight?.form?.[awayCanon] || [];
  const meetings = insight?.meetings?.[pairKey(insight, home, away)] || [];
  const homeYou = insight?.you?.[homeCanon];
  const awayYou = insight?.you?.[awayCanon];
  const lock = getFixturePredictionDeadline(match);
  const lockText = lock ? formatLock(lock) : "";
  const youBits = [
    homeYou?.picks ? `${shortTeamName(home)} ${Number(homeYou.avg).toFixed(1)} avg` : null,
    awayYou?.picks ? `${shortTeamName(away)} ${Number(awayYou.avg).toFixed(1)} avg` : null,
  ].filter(Boolean);

  const meetingText = meetings
    .map((item) => `${shortTeamName(item.home_team)} ${item.home_score}–${item.away_score} ${shortTeamName(item.away_team)}`)
    .join(" · ");

  return (
    <div className="flex flex-col gap-1 text-[13px] text-muted">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1.5 text-ink">
            {homePos ? <span className="tabular-nums">#{homePos}</span> : null}
            {shortTeamName(home)}
            <FormDots name={home} results={homeForm} />
          </span>
          <span className="inline-flex items-center gap-1.5 text-ink">
            {awayPos ? <span className="tabular-nums">#{awayPos}</span> : null}
            {shortTeamName(away)}
            <FormDots name={away} results={awayForm} />
          </span>
        </div>
        {lockText && <p className="shrink-0">Locks {lockText}</p>}
      </div>
      {(meetingText || youBits.length > 0) && (
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          {meetingText && <p className="min-w-0 text-ink">{meetingText}</p>}
          {youBits.length > 0 && <p className="shrink-0">You · {youBits.join(" · ")}</p>}
        </div>
      )}
    </div>
  );
}
